import { RowSelectionState } from "@tanstack/react-table";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import uniq from "lodash/uniq";
import { isAudit } from "PFApp/activities/helpers";
import { useIsReassignAllowed } from "PFApp/booking/components/details_panel/detail_view/reassign_side_panel/hooks/use_is_reassign_allowed";
import { ProfileSource } from "PFApp/components/profile_source_toggle";
import { useTemplateFind } from "PFApp/hooks";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useMatchesInvalidate } from "PFCore/hooks/queries/matches/use_matches_invalidate";
import { PermissionRule } from "PFTypes";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUnmount } from "react-use";

import { useReassignSubmit } from "../../../../parts/overview/booking_module_calendar/context/use_reassign_submit";
import { ReassignSelectOption } from "../../../reassign_booking_modal/bookings_to_reassign_selector.types";
import { DetailsSidePanelProps } from "../../details_panel_item";
import { DetailsHeader } from "../details_header";
import { ReassignContent } from "./components/reassign_content";
import { ReassignSidePanelFooter } from "./components/reassign_side_panel_footer";

export const ReassignSidePanel = React.memo(
  ({ isOnTop, onClose, show, zIndex, data, disableEntranceAnimation = false }: DetailsSidePanelProps) => {
    const { t } = useTranslation("bookingModule");
    const findTemplate = useTemplateFind();

    const reassignData = data.reassignDetails;
    const {
      allowedBookings: bookings,
      notAllowedBookings,
      allowedActivities: activities,
      notAllowedActivities,
      allowedTargets,
      isLoading
    } = useIsReassignAllowed({ bookingIds: reassignData?.bookingIds ?? [] });
    const [selectedTab, setSelectedTab] = useState<ProfileSource | undefined>(allowedTargets[0]);

    const sourceProfileIds = uniq(bookings.map(({ profile_id: profileId }) => profileId));
    const bookingActivityIds = compact(bookings.map(({ activity_id: activityId }) => activityId));

    const { invalidate: invalidateMatches } = useMatchesInvalidate();
    const { invalidateAllBookingsQueriedWithoutProfileId } = useBookingInvalidate();

    const handleClose = () => {
      onClose();
      setReassignSelectOption(ReassignSelectOption.ThisBooking);
    };

    const { handleSubmit: handleReassignSubmit } = useReassignSubmit({
      sourceProfileIds,
      bookings,
      onSubmit: reassignData?.onSubmit,
      bulkSelection: reassignData?.bulkSelection,
      reassignTarget: selectedTab
    });

    const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>({});
    const [reassignSelectOption, setReassignSelectOption] = useState<ReassignSelectOption>(
      ReassignSelectOption.ThisBooking
    );

    const canReassignBookingsAllResources = useIsCurrentUserPermittedTo(
      PermissionRule.ReassignBookingsAllResources
    );
    const canReassignBookingsMatches = useIsCurrentUserPermittedTo(PermissionRule.ReassignBookingsMatches);

    useEffect(() => {
      if (!isEmpty(bookingActivityIds)) {
        invalidateMatches(bookingActivityIds);
      }
    }, [JSON.stringify(bookingActivityIds)]);

    useUnmount(() => {
      invalidateAllBookingsQueriedWithoutProfileId();
    });

    if (!reassignData || (!canReassignBookingsAllResources && !canReassignBookingsMatches)) {
      return null;
    }

    const selectedRowIds = Object.keys(rowSelectionState);
    const handleSubmit = () => {
      if (selectedRowIds.length === 1) {
        const isAuditActivity =
          activities.length === 1 && isAudit(findTemplate({ id: activities[0].template_id }));

        if (isAuditActivity) {
          const auditActivity = activities[0];
          data.bookingModuleDetails?.openReassignModal?.({
            activity: auditActivity,
            activityName: auditActivity.name,
            sourceProfileId: Number(sourceProfileIds[0]),
            targetProfileId: Number(selectedRowIds[0]),
            bookings,
            bulkSelection: reassignData?.bulkSelection ?? undefined,
            reassignTarget: selectedTab,
            onClose: () => {
              handleClose();
            }
          });
        } else {
          handleReassignSubmit(reassignSelectOption, parseInt(selectedRowIds[0]));
          handleClose();
        }
      }
    };

    const ignoredBookingsCount = reassignData.bookingIds.length - bookings.length;

    return (
      <SidePanel
        size="medium"
        show={show}
        onClose={handleClose}
        title={
          <DetailsHeader
            title={
              bookings.length === 1 && bookings[0].title
                ? bookings[0].title
                : t("bookings.reassign.bookingsCount", { count: bookings.length })
            }
            subtitle={t("bookings.reassign.reassignAction")}
          />
        }
        zIndex={zIndex}
        isOnTop={isOnTop}
        disableEntranceAnimation={disableEntranceAnimation}
        footerRenderer={({ onSidePanelClose }) => (
          <ReassignSidePanelFooter
            submitDisabled={selectedRowIds.length !== 1 || isLoading}
            onClose={onSidePanelClose}
            onSubmit={handleSubmit}
          />
        )}
      >
        {isLoading ? (
          <LoadingDots />
        ) : (
          <ReassignContent
            key={JSON.stringify(reassignData.bookingIds)} // to remount the content as we want it to be fresh after data changes
            bookings={bookings}
            notAllowedBookings={notAllowedBookings}
            activities={activities}
            notAllowedActivities={notAllowedActivities}
            sourceProfileIds={sourceProfileIds}
            onTargetProfileChange={setRowSelectionState}
            rowSelectionState={rowSelectionState}
            reassignOption={reassignSelectOption}
            setReassignOption={setReassignSelectOption}
            allowedTargets={allowedTargets}
            ignoredBookingsCount={ignoredBookingsCount}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        )}
      </SidePanel>
    );
  }
);

ReassignSidePanel.displayName = "ReassignSidePanel";
